import './ListPage.scss';
import { Button, Icon, SimpleNumberCard, Checkbox } from '@design-system/index';
import {
    ArchiveLine,
    ArchiveFilled,
    SuiviBilleterieLine,
    AddDashed,
    UnarchiveFilled,
    UnarchiveLine,
    PrinterFull,
    PrinterLine
} from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { ColumnDef } from '@tanstack/react-table';
import { formatDate } from '@utils/dates';
import { LargeIconButton } from '@design-system/components/Buttons/LargeIconButton/LargeIconButton';
import { useListPageVM } from './useListPageVM';
import { TicketingTable } from '@services/member/ticketing/ticketing.types';
import { TicketType } from '@services/member/list/ticket-types/ticket-types.types';
import { formatMoney } from '@utils/numbers';

export default function ListPage() {
    const {
        handleToggleArchiveTicketing,
        handleToggleArchiveTicketings,
        handleMetadata,
        navigate,
        columnHelper,
        isArchived,
        ticketTypes,
        setIsArchived,
        totalMembers,
        mutationHook,
        isLoading,
        needReload,
        exportMutationHook,
        handlePrint
    } = useListPageVM();

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor((row) => row.member?.fullname ?? '', {
            header: 'Adhérent',
            id: 'member.personalInformation.lastname',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor((row) => row.ticketType.label, {
            id: 'ticketType.label',
            header: 'Type de billet',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor((row) => row.ticketingAt, {
            id: 'ticketingAt',
            header: 'Date de vente',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{formatDate(info.getValue())}</>
        }),
        columnHelper.accessor((row) => row.saleCount, {
            id: 'saleCount',
            header: 'Nombre de billets',
            meta: {
                clickable: true
            },
            enableSorting: false,
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor((row) => row.saleAmount, {
            id: 'saleAmount',
            header: 'Montant total',
            meta: {
                clickable: true
            },
            enableSorting: false,
            cell: (info) => <>{formatMoney(info.getValue())}</>
        }),
        columnHelper.accessor(
            (row) => (
                <>
                    <div>{row.firstTicketNumber}</div>
                    <div>{row.lastTicketNumber}</div>
                </>
            ),
            {
                id: 'firstTicketNumber',
                header: 'Numéros des billets',
                meta: {
                    clickable: true
                },
                enableSorting: true,
                cell: (info) => <>{info.getValue()}</>
            }
        ),
        columnHelper.display({
            id: 'archive',
            size: 0,
            header: isArchived ? 'Désarchiver' : 'Archiver',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={
                                isArchived ? (
                                    <Icon icon={<UnarchiveFilled />} size={20} color="primary" />
                                ) : (
                                    <Icon icon={<ArchiveFilled />} size={20} color="primary" />
                                )
                            }
                            icon={
                                isArchived ? (
                                    <Icon icon={<UnarchiveLine />} size={20} color="primary" />
                                ) : (
                                    <Icon icon={<ArchiveLine />} size={20} color="primary" />
                                )
                            }
                            onClick={() => handleToggleArchiveTicketing(info.row.original.id, !isArchived)}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<TicketingTable>[];

    return (
        <div className="members-table-container">
            <div className="members-table-container-header">
                <div className="members-table-container-header-left">
                    <SimpleNumberCard
                        labelForSingle={isArchived ? 'Vente archivée' : 'Vente'}
                        labelForMultiple={isArchived ? 'Ventes archivées' : 'Ventes'}
                        value={totalMembers}
                    />
                    <LargeIconButton
                        icon={<AddDashed />}
                        label="Nouvelle vente"
                        onClick={() => navigate('/members-management/ticketings/add-multi')}
                    />
                </div>
                <Button variant="secondary" onClick={() => setIsArchived(!isArchived)}>
                    {isArchived ? 'Actifs' : 'Archivés'}
                </Button>
            </div>
            <MutationTable
                key={isArchived ? 'archived' : 'active'}
                columns={columns}
                forceLoading={isLoading}
                needReload={needReload}
                mutationHook={mutationHook}
                onRowClick={(row) => navigate(`/members-management/ticketings/${row.id}/edit`)}
                defaultSorting={{
                    field: 'ticketingAt',
                    direction: 'desc'
                }}
                deleting={{
                    enabled: isArchived,
                    entity: 'ticketing'
                }}
                exportable={true}
                exportMutationHook={exportMutationHook}
                headerTitle={isArchived ? 'Archives des billets vendus' : 'Liste des billets vendus'}
                headerIcon={<SuiviBilleterieLine />}
                headerIconColor="turquoise"
                onMetadata={handleMetadata}
                filters={[
                    {
                        id: 'ticketingAt',
                        label: 'Date de vente',
                        type: 'date',
                        comparatorTypes: ['isBetween', 'isBefore', 'isAfter', 'equals']
                    },
                    {
                        id: 'ticketType',
                        label: 'Type de billet',
                        type: 'select',
                        options: ticketTypes.map((ticketType: TicketType) => ({ label: ticketType.label, value: ticketType.id.toString() }))
                    }
                ]}
                footer={{
                    actions: [
                        {
                            label: isArchived ? 'Désarchiver' : 'Archiver',
                            icon: isArchived ? (
                                <Icon icon={<UnarchiveLine />} size={20} color="primary" />
                            ) : (
                                <Icon icon={<ArchiveLine />} size={20} color="primary" />
                            ),
                            hoverIcon: isArchived ? (
                                <Icon icon={<UnarchiveFilled />} size={20} color="primary" />
                            ) : (
                                <Icon icon={<ArchiveFilled />} size={20} color="primary" />
                            ),
                            onClick: (lines: TicketingTable[]) => handleToggleArchiveTicketings(lines, !isArchived)
                        },
                        {
                            label: 'Imprimer',
                            icon: <Icon icon={<PrinterLine />} size={20} color="primary" />,
                            hoverIcon: <Icon icon={<PrinterFull />} size={20} color="primary" />,
                            onClick: (lines: TicketingTable[]) => handlePrint(lines)
                        }
                    ]
                }}
            />
        </div>
    );
}
